/**
 * QuoteBuilder.jsx — Main quote builder orchestrator
 *
 * Two modes:
 * - RE (Real Estate): Select from service catalog, auto-pricing by sq ft
 * - Custom: Manual line items with custom pricing
 *
 * Layout: Left panel (builder) + Right panel (live preview)
 */

import { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuoteBuilder } from '../../hooks/useQuoteBuilder';
import { useSettings, useUpdateSettings } from '../../hooks/useSettings';
import { useJobs, useCreateJob, useUpdateJob } from '../../hooks/useJobs';
import { WEB_LEAD_SOURCES } from '../../lib/webLeads';
import { useContacts, useUpdateContact } from '../../hooks/useContacts';
import { useRoster } from '../../hooks/useRoster';
import { useCreateQuote, useUpdateQuote, useQuotes } from '../../hooks/useQuotes';
import { useToast } from '../layout/Toast';
import { fmtC, uid } from '../../lib/utils';
import { api } from '../../lib/api';
import { track } from '../../lib/telemetry';
import { VIRTOUR_DB } from '../../data/virtourCatalog';
import AddressAutocomplete from '../shared/AddressAutocomplete';
import { useMobile } from '../../hooks/useMobile';
import { useFocus } from '../../hooks/useFocus';
import FocusHide from '../shared/FocusHide';

import ServiceSelector from './ServiceSelector';
import SiteLinkImport from './SiteLinkImport';
import QuoteLineItems from './QuoteLineItems';
import QuoteTerms from './QuoteTerms';
import QuotePreview from './QuotePreview';
import CreateJobConfirmDialog from './CreateJobConfirmDialog';
import JobIdentityBlockDialog from './JobIdentityBlockDialog';
import AddClientDialog from './AddClientDialog';
import { generateQuotePdf } from '../../lib/pdfGenerator';
import { snapshotTerms, getEffectiveLibrary } from '../../lib/quoteTerms';
import { buildVirtourDisplayLines } from '../../lib/virtourQuote';
import PdfPreviewModal from '../common/PdfPreviewModal';
import SendQuoteModal from './SendQuoteModal';

// Contact types the client picker offers, in display order. Crew are included
// because a crew member can also be a paying client — see the picker memo.
const CLIENT_PICKER_TYPES = ['client', 'crew'];

const TYPE_BADGE = {
  // amber-600 rather than the app's usual amber-400 chip: this badge is a
  // warning on a white surface, where 400 sits near 2:1 contrast.
  crew: { label: 'Crew', className: 'bg-amber-500/15 text-amber-600 border-amber-500/40' },
};

// Build a LOAD_FROM_JOB payload that only includes fields the job actually has.
// Used when supplementing an already-loaded quote with job data: empty job
// columns must not overwrite values restored from the quote's calcSnapshot
// (e.g. sqft, propertyType, bedrooms/bathrooms on older jobs that predate the
// sqft column or were created via quick-add).
function buildJobSupplementPayload(job) {
  // Deliberately no contactId: LOAD_FROM_JOB is a blind spread dispatched
  // *after* LOAD_FROM_QUOTE, so a job re-pointed at a different contact than
  // its quote would silently override the quote's own link — and since this
  // payload carries no `client`/`email`, the next save would pair one person's
  // name with another's email. The quote's link wins; a quote with none falls
  // back to the name match, as it did before.
  const src = {
    contact: job.contactName,
    phone: job.contactPhone,
    address: job.location,
    sqft: job.sqft,
    originator: job.originator,
    propertyType: job.propertyType,
    bedrooms: job.bedrooms,
    bathrooms: job.bathrooms,
    projectName: job.title,
    internalNotes: job.notes,
    workflowType: job.workflowType,
  };
  const payload = {};
  for (const [k, v] of Object.entries(src)) {
    if (v != null && v !== '') payload[k] = v;
  }
  return payload;
}

export default function QuoteBuilder() {
  const { state, dispatch, computed, activeTerms, rebuildRELines, buildJobData } = useQuoteBuilder();
  const { data: settings } = useSettings();
  const updateSettings = useUpdateSettings();
  const { data: jobs = [], isLoading: jobsLoading } = useJobs();
  const { data: contacts = [], isLoading: contactsLoading } = useContacts();
  const updateContact = useUpdateContact();
  const { data: roster = [] } = useRoster();
  const { data: allQuotes = [], isLoading: quotesLoading } = useQuotes();
  const createJob = useCreateJob();
  const updateJob = useUpdateJob();
  const createQuote = useCreateQuote();
  const updateQuoteMut = useUpdateQuote();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useMobile();
  const { isHidden } = useFocus();

  const [saving, setSaving] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // Server refused to re-point the linked job at a different client (409
  // job_identity_locked). Holds the refusal body so we can explain and offer
  // a separate job — or, for users the server lets override, a client change.
  const [identityBlock, setIdentityBlock] = useState(null);
  const [showAddClient, setShowAddClient] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientFocused, setClientFocused] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);
  // ID of the quote currently being sent via the SendQuoteModal pre-flight.
  // Captured after a Save & Send so the modal sees the freshly-persisted row.
  const [sendQuoteId, setSendQuoteId] = useState(null);

  // Ref-flag: skip first rebuildRELines after loading a quote
  const justLoadedRef = useRef(false);
  // Capture jobId from URL once on mount — stable across renders
  const initialJobIdRef = useRef(searchParams.get('jobId'));
  const initialQuoteIdRef = useRef(searchParams.get('quoteId'));
  const prefillDoneRef = useRef(false);

  // Focus mode hides these controls only while the quote has no value in them.
  // Once a value is seen they stay for the rest of that quote, so clearing the
  // field (or switching Staycation off) doesn't make it vanish mid-edit.
  const focusKeepRef = useRef({});
  const focusQuoteKey = `${state.sourceQuoteId}|${state.linkedJobId}|${state.quoteNumber}`;
  if (focusKeepRef.current.key !== focusQuoteKey) focusKeepRef.current = { key: focusQuoteKey };
  const focusKeep = focusKeepRef.current;
  if (state.internalNotes) focusKeep.internalNotes = true;
  if (state.propertyType || state.bedrooms || state.bathrooms) focusKeep.propertySelects = true;
  if (state.workflowType === 'staycation') focusKeep.staycation = true;
  const showStaycationToggle = !isHidden('quote.staycationToggle') || !!focusKeep.staycation;
  const showPropertySelects = !isHidden('quote.re.propertySelects') || !!focusKeep.propertySelects;
  const showInternalNotes = !isHidden('quote.internalNotes') || !!focusKeep.internalNotes;

  // ── Clean URL params immediately on mount ──
  useEffect(() => {
    const mode = searchParams.get('mode');
    const jobId = searchParams.get('jobId');
    const quoteId = searchParams.get('quoteId');
    if (mode || jobId || quoteId) {
      if (mode && (mode === 're' || mode === 'custom' || mode === 'virtour') && state.mode !== mode) {
        dispatch({ type: 'SET_MODE', payload: mode });
      }
      searchParams.delete('jobId');
      searchParams.delete('quoteId');
      searchParams.delete('mode');
      setSearchParams(searchParams, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount-only

  // ── Warn before closing tab with unsaved work ──
  useEffect(() => {
    const handler = (e) => {
      if (state.client || computed.lineCount > 0) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [state.client, computed.lineCount]);

  // ── Prefill from jobId (captured on mount) ──
  useEffect(() => {
    const jobId = initialJobIdRef.current;
    if (!jobId || prefillDoneRef.current) return;
    if (jobs.length === 0) return; // wait for jobs
    if (quotesLoading) return;      // wait for quotes

    prefillDoneRef.current = true;

    const job = jobs.find((j) => j.id === jobId);
    if (!job) return;

    // Find existing quote linked to this job (latest non-superseded, non-rejected)
    const existingQuote = allQuotes
      .filter((q) => q.jobId === jobId && q.status !== 'superseded' && q.status !== 'rejected')
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

    if (existingQuote) {
      dispatch({ type: 'LOAD_FROM_QUOTE', quote: existingQuote, jobId: job.id });
      justLoadedRef.current = true;
      // Supplement with job fields — but only for fields the job actually has,
      // so empty job columns don't wipe values already restored from the quote
      // (e.g. sqft, propertyType, bedrooms, bathrooms stored in calcSnapshot).
      dispatch({
        type: 'LOAD_FROM_JOB', jobId: job.id,
        payload: buildJobSupplementPayload(job),
      });
      toast('Loaded existing quote for: ' + (job.title || job.clientName));
    } else {
      const jobType = (job.jobType || '').toLowerCase();
      const jobMode = jobType.includes('virtour')
        ? 'virtour'
        : (jobType.includes('real estate') ? 're' : 'custom');
      dispatch({
        type: 'LOAD_FROM_JOB',
        jobId: job.id,
        payload: {
          mode: jobMode,
          contactId: job.contactId || '',
          client: job.clientName || '',
          contact: job.contactName || '',
          email: job.contactEmail || '',
          phone: job.contactPhone || '',
          address: job.location || '',
          sqft: job.sqft || '',
          originator: job.originator || '',
          propertyType: job.propertyType || '',
          bedrooms: job.bedrooms || '',
          bathrooms: job.bathrooms || '',
          projectName: job.title || '',
          internalNotes: job.notes || '',
          workflowType: job.workflowType || 'standard',
        },
      });
      toast('Prefilled from job: ' + (job.title || job.clientName));
    }
    try { sessionStorage.removeItem('rfos_quote_draft'); } catch {}
  }, [jobs, allQuotes, quotesLoading]);

  // ── Prefill from quoteId URL param (Edit in Builder from Saved Quotes) ──
  useEffect(() => {
    const quoteId = initialQuoteIdRef.current;
    if (!quoteId || prefillDoneRef.current) return;
    if (quotesLoading) return;

    const quote = allQuotes.find((q) => q.id === quoteId);
    if (!quote) return;

    prefillDoneRef.current = true;
    dispatch({ type: 'LOAD_FROM_QUOTE', quote, jobId: quote.jobId || null });
    justLoadedRef.current = true;

    // If quote has a linked job, also load job fields — but only those the job
    // actually has, so empty columns don't overwrite values already restored
    // from the quote (e.g. sqft).
    if (quote.jobId && jobs.length > 0) {
      const job = jobs.find((j) => j.id === quote.jobId);
      if (job) {
        dispatch({
          type: 'LOAD_FROM_JOB', jobId: job.id,
          payload: buildJobSupplementPayload(job),
        });
      }
    }
    toast('Editing quote: ' + (quote.quoteNumber || quote.clientName));
    try { sessionStorage.removeItem('rfos_quote_draft'); } catch {}
  }, [allQuotes, quotesLoading, jobs]);

  /**
   * Next free quote number for this year, scanning both jobs and quotes.
   * Extracted from the auto-generate effect below so the forceNewJob recovery
   * can mint a real number. It used to strip the -R suffix off the revision it
   * was recovering from, which handed the new client's quote the ORIGINAL
   * client's number — quotes.quote_number has no unique index, so two live
   * quotes for two different clients silently shared one.
   */
  const nextQuoteNumber = useCallback(() => {
    const prefix = settings?.data?.quotePrefix || 'RF-Q';
    const year = new Date().getFullYear();
    let maxSeq = (parseInt(settings?.data?.quoteStart, 10) || 1) - 1;
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`^${escaped}-${year}-(\\d+)(?:-R\\d+)?$`);
    for (const job of jobs) {
      const qn = job.quoteNumber || job.calcSnapshot?.quoteNumber;
      const m = qn?.match(pattern);
      if (m) maxSeq = Math.max(maxSeq, parseInt(m[1]));
    }
    for (const q of allQuotes) {
      const m = q.quoteNumber?.match(pattern);
      if (m) maxSeq = Math.max(maxSeq, parseInt(m[1]));
    }
    return `${prefix}-${year}-${String(maxSeq + 1).padStart(3, '0')}`;
  }, [settings, jobs, allQuotes]);

  // ── Auto-generate quote number ──
  useEffect(() => {
    if (state.quoteNumber || !settings || quotesLoading || jobsLoading) return;
    // Never mint a number for a record that already has one. The prefill effect
    // and this one run in the same pass, so this one still sees an empty number
    // and its dispatch lands last — overwriting the loaded quote's own number,
    // and with it the base a revision is numbered from (:533). Keyed on the data
    // rather than on prefill timing, which is why it used to differ run to run.
    const loadedQuote = initialQuoteIdRef.current
      ? allQuotes.find((q) => q.id === initialQuoteIdRef.current) : null;
    const loadedJob = initialJobIdRef.current
      ? jobs.find((j) => j.id === initialJobIdRef.current) : null;
    if (loadedQuote?.quoteNumber || loadedJob?.calcSnapshot?.quoteNumber) return;
    dispatch({ type: 'SET_FIELD', field: 'quoteNumber', value: nextQuoteNumber() });
  }, [settings, jobs, allQuotes, state.quoteNumber, dispatch, quotesLoading, jobsLoading, nextQuoteNumber]);

  // ── Rebuild RE lines when products/sqft change ──
  // NOTE: justLoadedRef is NOT cleared here — it stays true until the user
  // explicitly changes RE products/addons/sqft (via ServiceSelector callbacks).
  // This prevents React 18 StrictMode's double-mount from wiping lines after
  // LOAD_FROM_QUOTE when TanStack Query serves cached data immediately.
  useEffect(() => {
    if (justLoadedRef.current) return;
    if (state.mode === 're' || state.mode === 'virtour') {
      rebuildRELines();
    }
  }, [state.selectedProducts, state.productQuantities, state.selectedAddons, state.addonQuantities, state.sqft, state.rePriceVersion]);

  // ── Helpers ──
  const setField = useCallback((field, value) => {
    dispatch({ type: 'SET_FIELD', field, value });
  }, [dispatch]);

  // ── Client list (filtered by search) ──
  // Crew are billable here too: one person can be both a paid crew member and a
  // paying client, and excluding them made that unrepresentable — a contact
  // retyped client→crew silently vanished from this picker.
  const clientContacts = useMemo(() =>
    contacts
      .filter((c) => CLIENT_PICKER_TYPES.includes(c.type) && c.name)
      // Clients first so the default (unsearched) list is unchanged; crew are
      // reachable by typing and always carry a badge.
      .sort((a, b) =>
        (CLIENT_PICKER_TYPES.indexOf(a.type) - CLIENT_PICKER_TYPES.indexOf(b.type))
        || a.name.localeCompare(b.name)
      ),
    [contacts]
  );

  const filteredClients = useMemo(() => {
    if (!clientSearch) return clientContacts;
    const q = clientSearch.toLowerCase();
    return clientContacts.filter(
      (c) => c.name.toLowerCase().includes(q) || c.company?.toLowerCase().includes(q)
    );
  }, [clientContacts, clientSearch]);

  // Resolve the builder's client to a CRM row. contactId wins: it survives a
  // rename and a type change, both of which break the name/company fallback.
  // The fallback stays for quotes drafted before contactId was tracked.
  //
  // `canonical` says whether the row was reached by id. Only then may its name
  // overwrite the typed label — a fallback match on `company` legitimately has
  // a different label ("Goodwin & Thyne Properties" vs "Joy Martin"), and
  // rewriting that to the person's name would be a regression, not a repair.
  const resolveContact = useCallback(() => {
    const byId = state.contactId ? contacts.find((c) => c.id === state.contactId) : null;
    if (byId) return { contact: byId, canonical: true };
    const byName = contacts.find(
      (c) => CLIENT_PICKER_TYPES.includes(c.type)
        && (c.name === state.client || c.company === state.client)
    );
    return { contact: byName || null, canonical: false };
  }, [contacts, state.contactId, state.client]);

  // Keep the visible label in step with the linked CRM row. Without this, a
  // quote drafted before a rename shows the old name in the picker field, the
  // live preview, the confirm dialog and a builder-generated PDF, while the
  // save quietly persists the new one.
  useEffect(() => {
    if (!state.contactId) return;
    const linked = contacts.find((c) => c.id === state.contactId);
    if (linked?.name && linked.name !== state.client) {
      dispatch({ type: 'SET_CLIENT_INFO', payload: { client: linked.name, contact: linked.name } });
    }
  }, [contacts, state.contactId, state.client, dispatch]);

  const handleClientSelect = useCallback((contactOrName) => {
    const contact = typeof contactOrName === 'string'
      ? contacts.find((c) => c.name === contactOrName)
      : contactOrName;
    if (contact) {
      dispatch({
        type: 'SET_CLIENT_INFO',
        payload: {
          contactId: contact.id || '',
          client: contact.name,
          email: contact.email || '',
          phone: contact.phone || '',
          contact: contact.name,
        },
      });
      setClientSearch('');
    }
  }, [contacts, dispatch]);

  // ── VirTour: the client is always Gavin Palmer ──
  // Filled once, on the switch into VirTour mode, through the same action the
  // client picker uses — so a contact picked afterwards is never clobbered.
  // Silently skipped when the contact isn't in the CRM yet.
  const fillVirtourClient = useCallback(() => {
    const gavin =
      contacts.find((c) => (c.email || '').toLowerCase() === 'gavin@virtourmedia.com') ||
      contacts.find((c) => c.name === 'Gavin Palmer');
    if (gavin) handleClientSelect(gavin);
  }, [contacts, handleClientSelect]);

  // Fill Gavin on ANY entry into VirTour mode — the mode button, the sidebar's
  // ?mode=virtour deep link, or a job prefill — but never over a client that is
  // already set, and retry when the contacts list arrives after the switch
  // (fillVirtourClient's identity changes with `contacts`).
  useEffect(() => {
    if (state.mode !== 'virtour') return;
    if ((state.client || '').trim()) return;
    fillVirtourClient();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.mode, fillVirtourClient]);

  // ── Converting an existing quote to a catalog-priced mode ──
  // Rebuilding reprices off the catalog, so the total will move. On a job that
  // has already been billed or paid, that restates a number the invoice and the
  // crew ledger were derived from — say so before the lines are cleared.
  const convertPrompt = useCallback((label) => {
    const job = state.linkedJobId ? jobs.find((j) => j.id === state.linkedJobId) : null;
    const billed = ['invoiced', 'paid', 'archived'].includes(job?.status);
    return billed
      ? `This job is already ${job.status}. Rebuilding it as a ${label} quote will change the total, which is what the invoice and crew pay were based on.\n\nYour current lines stay visible as a reference. Continue?`
      : `Rebuild this as a ${label} quote?\n\nYour current lines are cleared but stay visible as a reference so you can see what you quoted before.`;
  }, [state.linkedJobId, jobs]);

  // ── VirTour: the PDF prints the payout, so it must be fed payout rows ──
  // pdfGenerator multiplies qty × rate per line and never branches on mode, so
  // the transform happens here and the printed rows sum to the printed total.
  const pdfState = useMemo(
    () => (state.mode === 'virtour'
      ? { ...state, lines: buildVirtourDisplayLines(state.lines) }
      : state),
    [state]
  );

  // ── VirTour: remember every split we entered ──
  // Keyed by product/package id in settings.data.virtourSplits (same store as
  // quoteTerms), last write wins, so the next quote prefills itself. Purely a
  // convenience: a failed write must never fail the quote save.
  const persistVirtourSplits = useCallback(async () => {
    if (state.mode !== 'virtour') return;
    const stored = settings?.data?.virtourSplits;
    const existing = stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {};
    const next = { ...existing };
    let changed = false;
    for (const line of computed.activeLines) {
      if (!line.productId || line.splitPct == null || line.splitPct === '') continue;
      const pct = parseFloat(line.splitPct);
      if (!Number.isFinite(pct) || next[line.productId] === pct) continue;
      next[line.productId] = pct;
      changed = true;
    }
    if (!changed) return;
    try {
      // Send ONLY this key. The server merges top-level, and echoing the whole
      // blob back would overwrite userPrefs with the caller-narrowed copy,
      // dropping every other user's preferences.
      await updateSettings.mutateAsync({ data: { virtourSplits: next } });
    } catch { /* learned defaults are a convenience — never block a save */ }
  }, [state.mode, computed.activeLines, settings, updateSettings]);

  // ── Library terms: labels (for collision check in Save-to-Library dialog) ──
  const libraryLabels = useMemo(() => {
    try {
      return getEffectiveLibrary(settings).map((t) => t.label).filter(Boolean);
    } catch {
      return [];
    }
  }, [settings]);

  // ── Promote a custom (per-quote) clause into the library so it shows up
  //    on future quotes. Writes to settings.data.quoteTerms, then swaps the
  //    custom term in local state with a reference to the new library term. ──
  const handlePromoteToLibrary = useCallback(
    async ({ customId, label, body, type, triggerCategories, enableByDefault }) => {
      const existingTerms = Array.isArray(settings?.data?.quoteTerms)
        ? settings.data.quoteTerms
        : [];

      // Generate next lib_<n> ID — mirror the pattern from QuoteTermsTab.handleAdd.
      const maxLib = existingTerms.reduce((max, t) => {
        const m = String(t?.id || '').match(/^lib_(\d+)$/);
        return m ? Math.max(max, parseInt(m[1], 10)) : max;
      }, 0);
      const newId = `lib_${maxLib + 1}`;

      const newTerm = {
        id: newId,
        isCustomLibrary: true,
        label: label.trim(),
        body: body || '',
        type: type || 'optional',
        triggerCategories: Array.isArray(triggerCategories) ? triggerCategories : [],
        on: !!enableByDefault,
      };

      await updateSettings.mutateAsync({
        ...settings,
        data: {
          ...(settings?.data || {}),
          quoteTerms: [...existingTerms, newTerm],
        },
      });

      dispatch({
        type: 'PROMOTE_CUSTOM_TO_LIBRARY',
        customId,
        libraryId: newId,
      });

      toast(`Saved \u201C${newTerm.label}\u201D to library`);
    },
    [settings, updateSettings, dispatch, toast]
  );

  // ── Save as Job (show confirmation first) ──
  const handleSaveAsJob = useCallback(() => {
    // Saving before the CRM list lands makes resolveContact() return null, which
    // used to mean "no contact" and silently detach the job from its client.
    if (contactsLoading) {
      toast('Still loading contacts — try again in a second', 'error');
      return;
    }
    if (computed.lineCount === 0) {
      toast('Add at least one line item', 'error');
      return;
    }
    if (state.mode === 'custom' && !state.projectName?.trim()) {
      toast('Enter a project name', 'error');
      return;
    }
    if (state.mode === 'virtour' && !state.address?.trim()) {
      toast('Enter the property address', 'error');
      return;
    }
    if (state.mode === 'virtour' && !(parseInt(state.sqft, 10) > 0)) {
      toast('Enter the square footage', 'error');
      return;
    }
    if (state.mode !== 'virtour' && state.discountValue > 0 && !state.discountReason?.trim()) {
      toast('Enter a reason for the discount', 'error');
      return;
    }
    setShowConfirm(true);
  }, [state.mode, state.projectName, state.address, state.sqft, state.discountValue, state.discountReason, computed.lineCount, contactsLoading, toast]);

  /**
   * @param {object} [opts]
   * @param {boolean} [opts.forceNewJob] Skip the "update the linked job" paths and
   *   create a fresh job + quote. Used to recover from a job_identity_locked 409,
   *   where the linked job belongs to a different client and must not be reused.
   * @param {boolean} [opts.allowIdentityChange] Update the linked job with the
   *   identity guard overridden — the other way out of that 409, when the client
   *   on this job really changed. The server honours it only for full-level
   *   users and logs it.
   */
  const handleConfirmCreateJob = useCallback(async (opts = {}) => {
    const forceNewJob = opts.forceNewJob === true;
    const identityOverride = opts.allowIdentityChange === true
      ? { allowIdentityChange: true, identityChangeReason: 'Client changed — confirmed from Quote Builder' }
      : {};
    setShowConfirm(false);
    setIdentityBlock(null);
    setSaving(true);
    try {
      const hasLinkedJob = !forceNewJob && !!state.linkedJobId;
      const hasSourceQuote = !forceNewJob && !!state.sourceQuoteId;
      const existingJob = hasLinkedJob ? jobs.find(j => j.id === state.linkedJobId) : null;
      // Fetch the job fresh before merging — the snapshot merge preserves pay
      // keys (splits, itemAllocs, itemSplits) written by the calculator, and a
      // stale cache here would silently revert them on requote
      let existingSnapshot = existingJob?.calcSnapshot;
      if (hasLinkedJob) {
        try {
          const fresh = await api.get(`/jobs/${state.linkedJobId}`);
          if (fresh?.calc_snapshot) existingSnapshot = fresh.calc_snapshot;
        } catch { /* fall back to cached snapshot */ }
      }
      const jobData = buildJobData(existingSnapshot);

      // A forced new job is not a revision of anything, so it gets a real number —
      // applied to BOTH the quote row and the job's snapshot, or the new job's
      // pipeline would read "Resend Revised Quote" for a quote never sent.
      const freshQuoteNumber = forceNewJob ? nextQuoteNumber() : null;
      if (freshQuoteNumber && jobData.calcSnapshot) {
        jobData.calcSnapshot = { ...jobData.calcSnapshot, quoteNumber: freshQuoteNumber };
      }

      // Match to CRM contact — by id first, so a contact renamed or retyped
      // since this quote was drafted still resolves.
      const { contact: match, canonical } = resolveContact();

      // The linked CRM row is the source of truth for the name. Without this, a
      // quote drafted before a rename writes its stale label back onto the job
      // and undoes the rename cascade (how "Max Herald" outlived "Maxx Hennard").
      if (match && canonical) {
        jobData.client = match.name;
        jobData.contact = match.name;
      }

      // Gap-fill CRM contact: update empty fields only, never overwrite existing data
      if (match) {
        const gapFill = {};
        if (!match.email && state.email) gapFill.email = state.email;
        if (!match.phone && state.phone) gapFill.phone = state.phone;
        if (Object.keys(gapFill).length > 0) {
          try { await updateContact.mutateAsync({ id: match.id, ...gapFill }); } catch { /* non-critical */ }
        }
      }

      // Shared quote payload
      const quotePayload = {
        quoteNumber: freshQuoteNumber ?? state.quoteNumber,
        clientName: (canonical && match?.name) || state.client,
        clientEmail: state.email,
        contactId: match?.id || null,
        mode: state.mode,
        workflowType: state.workflowType || 'standard',
        deliverableLines: computed.activeLines,
        addOns: Object.keys(state.selectedAddons || {}),
        shootDates: state.shootDates || [],
        subtotal: computed.subtotal,
        discountPct: jobData.discountPct,
        discountAmt: computed.discountAmount,
        discountReason: state.discountReason,
        total: computed.total,
        terms: snapshotTerms(activeTerms),
        notes: state.notes,
        calcSnapshot: jobData.calcSnapshot,
      };

      if (hasLinkedJob && hasSourceQuote) {
        // ── Path A: Update existing job + update/revise quote ──
        const { id, createdAt, status, ...jobUpdates } = jobData;
        // Always send a contact_id. Deleting the key when no CRM row matched
        // disarmed the server's identity guard — `undefined` skips the check —
        // while the payload still carried client, title, revenue and dates.
        // Falling back to the job's own contact keeps the guard armed and leaves
        // the existing link untouched.
        jobUpdates.contactId = match?.id ?? state.contactId ?? existingJob?.contactId ?? null;
        // Don't clobber fields Quote Builder doesn't expose. If buildJobData
        // emits empty strings for these, drop them so the existing job value
        // is preserved server-side.
        if (!jobUpdates.referredBy) delete jobUpdates.referredBy;
        if (!jobUpdates.deliveryLink) delete jobUpdates.deliveryLink;
        jobUpdates.updatedAt = new Date().toISOString();
        // Preserve web-lead provenance: a lead from the marketing site keeps
        // its source (the Inquiries inbox and analytics key on it).
        if (!WEB_LEAD_SOURCES.includes(existingJob?.source)) jobUpdates.source = 'quote';
        await updateJob.mutateAsync({ id: state.linkedJobId, ...jobUpdates, ...identityOverride });

        try {
          if (state.sourceQuoteStatus === 'draft') {
            await updateQuoteMut.mutateAsync({
              id: state.sourceQuoteId,
              ...quotePayload,
              jobId: state.linkedJobId,
              status: 'draft',
            });
          } else {
            const newRev = (state.sourceQuoteRevision || 1) + 1;
            const baseNumber = state.quoteNumber.replace(/-R\d+$/, '');
            await createQuote.mutateAsync({
              ...quotePayload,
              quoteNumber: `${baseNumber}-R${newRev}`,
              revision: newRev,
              parentQuoteId: state.sourceQuoteId,
              jobId: state.linkedJobId,
              status: 'draft',
            });
            await updateQuoteMut.mutateAsync({
              id: state.sourceQuoteId,
              status: 'superseded',
            });
          }
        } catch { /* quote save is non-critical */ }

        track('quote.saved', { mode: state.mode, path: 'update_job_revise_quote', total: computed.total, ...(state.mode === 're' ? { priceVersion: state.rePriceVersion } : {}) });
        toast('Job updated \u2014 ' + jobData.title);
        dispatch({ type: 'RESET' });
        navigate(`/jobs?select=${state.linkedJobId}`);
      } else if (hasLinkedJob && !hasSourceQuote) {
        // ── Path B: Update existing job + create first quote ──
        const { id, createdAt, status, ...jobUpdates } = jobData;
        // Always send a contact_id — see Path A.
        jobUpdates.contactId = match?.id ?? state.contactId ?? existingJob?.contactId ?? null;
        // Preserve fields Quote Builder doesn't expose (see Path A).
        if (!jobUpdates.referredBy) delete jobUpdates.referredBy;
        if (!jobUpdates.deliveryLink) delete jobUpdates.deliveryLink;
        jobUpdates.updatedAt = new Date().toISOString();
        // Preserve web-lead provenance (see Path A).
        if (!WEB_LEAD_SOURCES.includes(existingJob?.source)) jobUpdates.source = 'quote';
        await updateJob.mutateAsync({ id: state.linkedJobId, ...jobUpdates, ...identityOverride });

        try {
          await createQuote.mutateAsync({
            ...quotePayload,
            jobId: state.linkedJobId,
            status: 'draft',
          });
        } catch { /* quote save is non-critical */ }

        toast('Job updated \u2014 ' + jobData.title);
        dispatch({ type: 'RESET' });
        navigate(`/jobs?select=${state.linkedJobId}`);
      } else {
        // ── Path C: Create new job + create quote ──
        jobData.id = uid();
        jobData.createdAt = new Date().toISOString();
        jobData.updatedAt = new Date().toISOString();
        jobData.source = 'quote';
        // Insert, so contact_id must resolve to a live row — buildJobData now
        // carries state.contactId, which can be dangling if the contact was
        // deleted mid-session. No match means no link, not a stale id.
        jobData.contactId = match?.id || null;

        await createJob.mutateAsync(jobData);

        try {
          await createQuote.mutateAsync({
            ...quotePayload,
            jobId: jobData.id,
            status: 'draft',
          });
        } catch { /* quote save is non-critical */ }

        track('quote.saved', { mode: state.mode, path: forceNewJob ? 'recovered_new_job' : 'new_job', total: computed.total, ...(state.mode === 're' ? { priceVersion: state.rePriceVersion } : {}) });
        toast('Job created \u2014 ' + jobData.title);
        dispatch({ type: 'RESET' });
        navigate(`/jobs?select=${jobData.id}`);
      }

      // Learn this quote's VirTour splits for the next one (no-op elsewhere).
      await persistVirtourSplits();
    } catch (err) {
      // The linked job is committed to a different client. Don't show a raw
      // error — this is a fork in the road, and reusing that job is what
      // destroyed the Valle Verde job in September 2026.
      if (err?.code === 'job_identity_locked' || err?.code === 'quote_job_contact_mismatch') {
        // Worth counting: a rising number here means the UI is steering people
        // toward reusing a job it should have offered to split.
        track('job.identity_blocked', { code: err.code, mode: state.mode });
        setIdentityBlock(err.body || { error: err.message });
      } else {
        toast('Error saving job: ' + err.message, 'error');
      }
    } finally {
      setSaving(false);
    }
  }, [state, computed, buildJobData, contacts, resolveContact, jobs, createJob, updateJob, createQuote, updateQuoteMut, updateContact, activeTerms, persistVirtourSplits, dispatch, toast, navigate]);

  // Discount reason required when discount value > 0 (VirTour has no discount)
  const discountMissingReason =
    state.mode !== 'virtour' && state.discountValue > 0 && !state.discountReason?.trim();

  // Save the current builder state as a draft quote (used by Save Draft + Save & Send).
  // Returns the persisted quote object (with `id`) on success, null on failure.
  // Same logic that was previously inlined on the Save Draft button.
  const saveDraftAsync = useCallback(async () => {
    try {
      const { contact: match, canonical } = resolveContact();
      const draftPayload = {
        quoteNumber: state.quoteNumber,
        clientName: (canonical && match?.name) || state.client,
        clientEmail: state.email,
        contactId: match?.id || null,
        mode: state.mode,
        workflowType: state.workflowType || 'standard',
        status: 'draft',
        deliverableLines: computed.activeLines,
        addOns: Object.keys(state.selectedAddons || {}),
        shootDates: state.shootDates || [],
        subtotal: computed.subtotal,
        discountPct: state.mode === 'virtour' ? 0 : (state.discountType === 'pct' ? state.discountValue : 0),
        discountAmt: computed.discountAmount,
        discountReason: state.discountReason,
        total: computed.total,
        terms: snapshotTerms(activeTerms),
        notes: state.notes,
        calcSnapshot: buildJobData().calcSnapshot,
        updatedAt: new Date().toISOString(),
      };

      let saved;
      if (state.sourceQuoteId && state.sourceQuoteStatus === 'draft') {
        saved = await updateQuoteMut.mutateAsync({
          id: state.sourceQuoteId,
          ...draftPayload,
        });
        toast('Draft updated');
      } else {
        if (state.linkedJobId) draftPayload.jobId = state.linkedJobId;
        saved = await createQuote.mutateAsync(draftPayload);
        // Adopt the new quote as the source so a second Save Draft UPDATES it
        // rather than inserting a duplicate row.
        if (saved?.id) dispatch({ type: 'MARK_SAVED_QUOTE', id: saved.id, jobId: draftPayload.jobId });
        toast('Quote draft saved');
      }
      // Learn this quote's VirTour splits for the next one (no-op elsewhere).
      await persistVirtourSplits();
      // Whether the mutation returned the row or just {id}, normalise to a
      // shape SendQuoteModal can use (it re-reads from the cache via id anyway).
      return saved && saved.id
        ? saved
        : { id: state.sourceQuoteId || saved?.id, ...draftPayload };
    } catch (err) {
      toast('Failed to save draft: ' + err.message, 'error');
      return null;
    }
  }, [state, computed, activeTerms, contacts, resolveContact, createQuote, updateQuoteMut, buildJobData, persistVirtourSplits, dispatch, toast]);

  return (
    <div data-track-area="quote-builder" className="flex gap-4 h-full min-h-0">
      {/* ── Left: Builder Panel ── */}
      <div className="flex-1 min-w-0 space-y-5 overflow-y-auto pr-2 pb-32 md:pb-0">
        {/* Mode Toggle + Quote Metadata */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (state.mode === 're') return;
                if (computed.lineCount > 0 && !confirm(convertPrompt('Real Estate'))) return;
                justLoadedRef.current = false;
                dispatch({ type: 'CONVERT_MODE', payload: 're' });
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                state.mode === 're'
                  ? 'bg-accent text-white'
                  : 'bg-surface border border-border text-text-muted hover:text-text-primary'
              }`}
            >
              Real Estate
            </button>
            <button
              onClick={() => {
                if (state.mode === 'virtour') return;
                if (computed.lineCount > 0 && !confirm(convertPrompt('VirTour'))) return;
                justLoadedRef.current = false;
                dispatch({ type: 'CONVERT_MODE', payload: 'virtour' });
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                state.mode === 'virtour'
                  ? 'bg-accent text-white'
                  : 'bg-surface border border-border text-text-muted hover:text-text-primary'
              }`}
            >
              VirTour
            </button>
            <button
              onClick={() => {
                if (state.mode === 'custom') return;
                if (computed.lineCount > 0 && !confirm(convertPrompt('Custom'))) return;
                dispatch({ type: 'CONVERT_MODE', payload: 'custom' });
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                state.mode === 'custom'
                  ? 'bg-accent text-white'
                  : 'bg-surface border border-border text-text-muted hover:text-text-primary'
              }`}
            >
              Custom
            </button>

            {/* Staycation workflow flag — orthogonal to RE/Custom pricing mode.
                Marks the job/quote for the segment-based pay split + post-only
                pipeline. Persists to workflow_type on both quote and job. */}
            {showStaycationToggle && (
            <label
              data-focus="quote.staycationToggle"
              className={`ml-2 flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                state.workflowType === 'staycation'
                  ? 'bg-purple-500 text-white'
                  : 'bg-surface border border-border text-text-muted hover:text-text-primary'
              }`}
              title="Staycation workflow: segment-based pay split, post-only pipeline"
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={state.workflowType === 'staycation'}
                onChange={(e) => setField('workflowType', e.target.checked ? 'staycation' : 'standard')}
              />
              ✦ Staycation
            </label>
            )}
          </div>

          <div className="flex items-center gap-2 text-xs text-text-dim">
            <span className="mono">{state.quoteNumber}</span>
            <span>&middot;</span>
            <input
              type="date"
              value={state.quoteDate}
              onChange={(e) => setField('quoteDate', e.target.value)}
              className="field w-32 text-xs"
            />
          </div>
        </div>

        {/* Paste website link (RE): a website order fills the size and services */}
        {state.mode === 're' && (
          <SiteLinkImport
            sqft={state.sqft}
            priceVersion={state.rePriceVersion}
            hasServices={Object.keys(state.selectedProducts).length > 0
              || state.lines.some((l) => l.source === 're' || l.source === 're-addon')}
            currentReTotal={state.lines
              .filter((l) => l.source === 're' || l.source === 're-addon')
              .reduce((sum, l) => sum + (parseFloat(l.qty) || 0) * (parseFloat(l.rate) || 0), 0)}
            hasCustomLines={state.lines.some((l) => l.source === 'custom')}
            onImport={(selection) => {
              justLoadedRef.current = false;
              dispatch({ type: 'IMPORT_SITE_SELECTION', selection });
            }}
          />
        )}

        {/* Client Info */}
        <div className="bg-surface border border-border rounded-lg p-4">
          <h3 className="text-sm font-heading text-text-primary uppercase tracking-wide mb-3">
            Client Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Searchable client select */}
            <div className="relative">
              <label className="text-2xs text-text-dim uppercase mb-1 block">Select Client</label>
              <input
                type="text"
                value={state.client ? state.client : clientSearch}
                onChange={(e) => {
                  setClientSearch(e.target.value);
                  setClientFocused(true);
                  if (state.client) {
                    dispatch({ type: 'SET_CLIENT_INFO', payload: { contactId: '', client: '', email: '', phone: '', contact: '' } });
                  }
                }}
                onFocus={() => {
                  setClientFocused(true);
                  if (state.client) setClientSearch(state.client);
                }}
                onBlur={() => setTimeout(() => setClientFocused(false), 200)}
                placeholder="Search clients & crew..."
                className="field w-full text-sm"
              />
              {/* Dropdown list */}
              {clientFocused && !state.client && (
                <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-surface border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredClients.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-text-dim">No contacts found</div>
                  ) : (
                    filteredClients.slice(0, 20).map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        data-track="quote.clientPick"
                        onClick={() => handleClientSelect(c)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-surface-2 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-text-primary">{c.name}</span>
                          {TYPE_BADGE[c.type] && (
                            <span className={`text-2xs uppercase tracking-wide px-1.5 py-0.5 rounded border ${TYPE_BADGE[c.type].className}`}>
                              {TYPE_BADGE[c.type].label}
                            </span>
                          )}
                        </div>
                        {c.company && <div className="text-xs text-text-dim">{c.company}</div>}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Add New Client button */}
            <div className="flex items-end">
              <button
                type="button"
                onClick={() => setShowAddClient(true)}
                className="px-4 py-2 text-sm bg-surface border border-accent/40 text-accent rounded hover:bg-accent/10 transition-colors"
              >
                + Add New Client
              </button>
            </div>

            {/* Editable email/phone when client selected */}
            {state.client && (
              <>
                <div>
                  <label className="text-2xs text-text-dim uppercase mb-1 block">Email</label>
                  <input
                    type="email"
                    value={state.email}
                    onChange={(e) => setField('email', e.target.value)}
                    placeholder="client@example.com"
                    className="field w-full text-sm"
                  />
                </div>
                <div>
                  <label className="text-2xs text-text-dim uppercase mb-1 block">Phone</label>
                  <input
                    type="tel"
                    value={state.phone}
                    onChange={(e) => setField('phone', e.target.value)}
                    placeholder="(555) 555-5555"
                    className="field w-full text-sm"
                  />
                </div>
              </>
            )}

            <div>
              <label className="text-2xs text-text-dim uppercase mb-1 block">Originator</label>
              <select
                value={state.originator}
                onChange={(e) => setField('originator', e.target.value)}
                className="field w-full text-sm"
              >
                <option value="">Select crew member</option>
                {roster
                  .filter((r) => r.name)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((r) => (
                    <option key={r.id} value={r.name}>{r.name}</option>
                  ))
                }
              </select>
            </div>

            {state.mode === 'custom' && (
              <>
                <div>
                  <label className="text-2xs text-text-dim uppercase mb-1 block">Contact Person</label>
                  <input
                    type="text"
                    value={state.contact}
                    onChange={(e) => setField('contact', e.target.value)}
                    placeholder="Contact name"
                    className="field w-full text-sm"
                  />
                </div>
                <div>
                  <label className="text-2xs text-text-dim uppercase mb-1 block">Project Name</label>
                  <input
                    type="text"
                    value={state.projectName}
                    onChange={(e) => setField('projectName', e.target.value)}
                    placeholder="Project title"
                    className="field w-full text-sm"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Property Info (RE + VirTour — only sq ft drives VirTour pricing) */}
        {(state.mode === 're' || state.mode === 'virtour') && (
          <div className="bg-surface border border-border rounded-lg p-4">
            <h3 className="text-sm font-heading text-text-primary uppercase tracking-wide mb-3">
              Property Info
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="text-2xs text-text-dim uppercase mb-1 block">Property Address</label>
                <AddressAutocomplete
                  value={state.address}
                  onChange={(v) => setField('address', v)}
                  placeholder="123 Main St, Santa Barbara, CA"
                  className="field w-full text-sm"
                />
              </div>
              <div>
                <label className="text-2xs text-text-dim uppercase mb-1 block">Square Footage</label>
                <input
                  type="number"
                  value={state.sqft}
                  onChange={(e) => setField('sqft', e.target.value)}
                  placeholder="e.g. 2500"
                  className="field w-full text-sm"
                />
                {/* VirTour's form has no rung above 9,999 sq ft, so tier prices
                    clamp to the top rung — the payout basis may be understated */}
                {state.mode === 'virtour' && parseInt(state.sqft, 10) >= 10000 && (
                  <p className="text-2xs text-amber-400 mt-1">
                    VirTour's order form tops out at 9,000–9,999 sq ft. Prices below use that
                    rung — verify the retail against what VirTour actually billed.
                  </p>
                )}
              </div>
              {state.mode === 're' && showPropertySelects && (
              <>
              <div data-focus="quote.re.propertySelects">
                <label className="text-2xs text-text-dim uppercase mb-1 block">Property Type</label>
                <select
                  value={state.propertyType}
                  onChange={(e) => setField('propertyType', e.target.value)}
                  className="field w-full text-sm"
                >
                  <option value="">Select type (optional)</option>
                  <option value="Single Family Home">Single Family Home</option>
                  <option value="Condo / Townhouse">Condo / Townhouse</option>
                  <option value="Multi-Family">Multi-Family</option>
                  <option value="Luxury Estate">Luxury Estate</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Land / Lot">Land / Lot</option>
                  <option value="New Construction">New Construction</option>
                </select>
              </div>
              <div data-focus="quote.re.propertySelects">
                <label className="text-2xs text-text-dim uppercase mb-1 block">Bedrooms</label>
                <select
                  value={state.bedrooms}
                  onChange={(e) => setField('bedrooms', e.target.value)}
                  className="field w-full text-sm"
                >
                  <option value="">— (optional)</option>
                  {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                    <option key={n} value={String(n)}>{n}{n === 10 ? '+' : ''}</option>
                  ))}
                </select>
              </div>
              <div data-focus="quote.re.propertySelects">
                <label className="text-2xs text-text-dim uppercase mb-1 block">Bathrooms</label>
                <select
                  value={state.bathrooms}
                  onChange={(e) => setField('bathrooms', e.target.value)}
                  className="field w-full text-sm"
                >
                  <option value="">— (optional)</option>
                  {[1,1.5,2,2.5,3,3.5,4,4.5,5,6,7,8,9,10].map((n) => (
                    <option key={n} value={String(n)}>{n}{n === 10 ? '+' : ''}</option>
                  ))}
                </select>
              </div>
              </>
              )}
            </div>
          </div>
        )}

        {/* Internal Notes — NOT on PDF */}
        {showInternalNotes && (
        <div data-focus="quote.internalNotes" className="bg-surface border border-border rounded-lg p-4">
          <h3 className="text-sm font-heading text-text-primary uppercase tracking-wide mb-3">
            Internal Notes
          </h3>
          <textarea
            value={state.internalNotes}
            onChange={(e) => setField('internalNotes', e.target.value)}
            placeholder="Internal notes (not visible on quote/PDF)..."
            rows={2}
            className="field w-full text-sm resize-y"
          />
        </div>
        )}

        {/* What this quote was before conversion — reference only, never priced.
            Sits above the catalog so it is the first thing seen after converting,
            and stays in reading order: what it was, then what to rebuild it from. */}
        {state.convertedFrom && state.convertedFrom.lines?.length > 0 && (
          <div className="bg-surface border border-dashed border-border rounded-lg p-4">
            <div className="flex items-baseline justify-between mb-3 gap-3">
              <h3 className="text-sm font-heading text-text-muted uppercase tracking-wide">
                Previously quoted
                {state.convertedFrom.quoteNumber ? ` · ${state.convertedFrom.quoteNumber}` : ''}
              </h3>
              <button
                onClick={() => dispatch({ type: 'CLEAR_CONVERTED_FROM' })}
                className="text-2xs text-text-dim hover:text-text-primary transition-colors"
                title="Hide this reference"
              >
                Dismiss
              </button>
            </div>
            <div className="space-y-1">
              {state.convertedFrom.lines.map((l, i) => (
                <div key={i} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="text-text-muted truncate">
                    {l.qty > 1 ? `${l.qty} × ` : ''}{l.desc}
                  </span>
                  <span className="text-text-muted tabular-nums whitespace-nowrap">{fmtC(l.amount)}</span>
                </div>
              ))}
            </div>
            <div className="flex items-baseline justify-between gap-3 mt-2 pt-2 border-t border-border text-sm">
              <span className="text-text-dim">
                Previous total{state.convertedFrom.mode === 'custom' ? ' (custom)' : ''}
              </span>
              <span className="text-text-primary tabular-nums">{fmtC(state.convertedFrom.total)}</span>
            </div>
            {state.mode === 'virtour' && computed.payoutTotal > 0 && (
              <div className="flex items-baseline justify-between gap-3 mt-1 text-sm">
                <span className="text-text-dim">Rebuilt payout</span>
                <span className="tabular-nums text-text-primary">
                  {fmtC(computed.payoutTotal)}
                  <span className={`ml-2 text-2xs ${computed.payoutTotal >= state.convertedFrom.total ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {computed.payoutTotal >= state.convertedFrom.total ? '+' : ''}
                    {fmtC(computed.payoutTotal - state.convertedFrom.total)}
                  </span>
                </span>
              </div>
            )}
          </div>
        )}

        {/* RE Mode: Service Selector (3×2 grid with inline add-ons) */}
        {state.mode === 're' && (
          <ServiceSelector
            // The Old / New price-list tab: the whole quote sells from one.
            priceVersion={state.rePriceVersion}
            currentReTotal={state.lines
              .filter((l) => l.source === 're' || l.source === 're-addon')
              .reduce((sum, l) => sum + (parseFloat(l.qty) || 0) * (parseFloat(l.rate) || 0), 0)}
            onSetPriceVersion={(v) => {
              justLoadedRef.current = false;
              dispatch({ type: 'SET_RE_PRICE_VERSION', priceVersion: v });
            }}
            selectedProducts={state.selectedProducts}
            productQuantities={state.productQuantities}
            selectedAddons={state.selectedAddons}
            addonQuantities={state.addonQuantities}
            sqft={state.sqft}
            onToggleProduct={(id) => {
              justLoadedRef.current = false;
              dispatch({ type: 'TOGGLE_PRODUCT', productId: id });
            }}
            onToggleAddon={(id) => {
              justLoadedRef.current = false;
              dispatch({ type: 'TOGGLE_ADDON', addonId: id });
            }}
            onSetAddonQty={(id, qty) => {
              justLoadedRef.current = false;
              dispatch({ type: 'SET_ADDON_QTY', addonId: id, qty });
            }}
            onSetSqft={(v) => {
              justLoadedRef.current = false;
              setField('sqft', v);
            }}
          />
        )}

        {/* VirTour Mode: same selector, driven by the mirrored VirTour catalog */}
        {state.mode === 'virtour' && (
          <ServiceSelector
            catalog={VIRTOUR_DB}
            selectedProducts={state.selectedProducts}
            sqft={state.sqft}
            // The picker sells each item from one of two price columns, so the
            // version rides along with the id. RE passes none and the reducer
            // defaults it.
            onToggleProduct={(id, priceVersion) => {
              justLoadedRef.current = false;
              dispatch({ type: 'TOGGLE_PRODUCT', productId: id, priceVersion });
            }}
            onSetSqft={(v) => {
              justLoadedRef.current = false;
              setField('sqft', v);
            }}
          />
        )}

        {/* Line Items + Discount (merged card) */}
        <div className="bg-surface border border-border rounded-lg p-4">
          <h3 className="text-sm font-heading text-text-primary uppercase tracking-wide mb-3">
            {state.mode === 're' ? 'Selected Services' : (state.mode === 'virtour' ? 'VirTour Order' : 'Line Items')}
          </h3>
          <QuoteLineItems
            lines={state.lines}
            mode={state.mode}
            onUpdateLine={(lineId, updates) =>
              dispatch({ type: 'UPDATE_LINE', lineId, updates })
            }
            onRemoveLine={(lineId) => dispatch({ type: 'REMOVE_LINE', lineId })}
            onAddLine={() =>
              dispatch({
                type: 'ADD_LINE',
                source: 'custom',
                desc: '',
                qty: 1,
                rate: 0,
                dur: 0,
              })
            }
            onUpdateSchedule={(lineId, entryIdx, updates) =>
              dispatch({ type: 'UPDATE_SCHEDULE', lineId, entryIdx, updates })
            }
            onAddScheduleEntry={(lineId) =>
              dispatch({ type: 'ADD_SCHEDULE_ENTRY', lineId })
            }
            onRemoveScheduleEntry={(lineId, entryIdx) =>
              dispatch({ type: 'REMOVE_SCHEDULE_ENTRY', lineId, entryIdx })
            }
            onResetLineRate={(lineId) =>
              dispatch({ type: 'RESET_LINE_RATE', lineId })
            }
            onMoveLine={(fromIndex, toIndex) =>
              dispatch({ type: 'MOVE_LINE', fromIndex, toIndex })
            }
          />

          {/* Discount — inline. Absent in VirTour: retail → split → payout is
              the whole transform, so there is nothing left to discount. */}
          {state.mode !== 'virtour' && (
          <div className="mt-4 pt-3 border-t border-border">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs font-heading text-text-primary uppercase tracking-wide">Discount</span>
              <select
                value={state.discountType}
                onChange={(e) => dispatch({ type: 'SET_DISCOUNT', discountType: e.target.value })}
                className="field w-20 text-sm"
              >
                <option value="pct">%</option>
                <option value="flat">$</option>
              </select>
              <input
                type="number"
                min="0"
                step={state.discountType === 'pct' ? '1' : '0.01'}
                max={state.discountType === 'pct' ? '100' : undefined}
                value={state.discountValue || ''}
                onChange={(e) =>
                  dispatch({ type: 'SET_DISCOUNT', discountValue: parseFloat(e.target.value) || 0 })
                }
                placeholder={state.discountType === 'pct' ? '0' : '0.00'}
                className="field w-20 text-sm"
              />
              <input
                type="text"
                value={state.discountReason}
                onChange={(e) => dispatch({ type: 'SET_DISCOUNT', discountReason: e.target.value })}
                placeholder="Reason for discount"
                className={`field flex-1 min-w-[140px] text-sm ${discountMissingReason ? 'border-red-500 ring-1 ring-red-500' : ''}`}
              />
              {discountMissingReason && (
                <span className="text-red-500 text-xs">Required</span>
              )}
              {computed.discountAmount > 0 && (
                <span className="text-green text-sm font-medium">
                  -{fmtC(computed.discountAmount)}
                </span>
              )}
            </div>
          </div>
          )}
        </div>

        {/* Terms (collapsible) */}
        <div className="bg-surface border border-border rounded-lg p-4">
          <button
            onClick={() => setShowTerms(!showTerms)}
            className="flex items-center justify-between w-full"
          >
            <h3 className="text-sm font-heading text-text-primary uppercase tracking-wide">
              Terms &amp; Conditions
            </h3>
            <span className="text-text-dim text-xs">
              {activeTerms.filter((t) => t.on).length} active {showTerms ? '\u25B2' : '\u25BC'}
            </span>
          </button>
          {showTerms && (
            <div className="mt-3">
              <QuoteTerms
                activeTerms={activeTerms}
                selectedCategoryIds={computed.selectedCategoryIds}
                libraryLabels={libraryLabels}
                onToggleTerm={(termId) => dispatch({ type: 'TOGGLE_TERM', termId })}
                onEditTermBody={(termId, updates) =>
                  dispatch({ type: 'SET_TERM_OVERRIDE', termId, ...updates })
                }
                onResetTerm={(termId) =>
                  dispatch({ type: 'CLEAR_TERM_OVERRIDE', termId })
                }
                onAddCustom={({ label, body, note }) =>
                  dispatch({ type: 'ADD_CUSTOM_TERM', label, body, note })
                }
                onUpdateCustom={(id, updates) =>
                  dispatch({ type: 'UPDATE_CUSTOM_TERM', id, ...updates })
                }
                onRemoveCustom={(id) =>
                  dispatch({ type: 'REMOVE_CUSTOM_TERM', id })
                }
                onPromoteToLibrary={handlePromoteToLibrary}
                onReorder={(orderedIds) =>
                  dispatch({ type: 'REORDER_TERMS', orderedIds })
                }
              />
            </div>
          )}

          {/* Quote Notes — appears on PDF */}
          <div className="mt-4">
            <label className="text-2xs text-text-dim uppercase mb-1 block">Quote Notes (included on PDF)</label>
            <textarea
              value={state.notes}
              onChange={(e) => setField('notes', e.target.value)}
              placeholder="Notes visible to the client on the quote/PDF..."
              rows={2}
              className="field w-full text-sm resize-y"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-3 pb-6">
          <button
            onClick={handleSaveAsJob}
            disabled={saving || createJob.isPending || updateJob.isPending || !state.client || computed.lineCount === 0}
            className="btn-primary px-6 py-2.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving
              ? (state.linkedJobId ? 'Updating...' : 'Creating...')
              : (state.linkedJobId ? 'Update Job' : 'Save as Job')}
          </button>
          <button
            onClick={async () => {
              if (discountMissingReason) {
                toast('Enter a reason for the discount', 'error');
                return;
              }
              try {
                const r = await generateQuotePdf({ state: pdfState, computed, activeTerms, settings, returnBlob: true });
                setPdfPreview(r);
              } catch (err) {
                console.error('PDF preview failed:', err);
                toast('Failed to generate PDF', 'error');
              }
            }}
            disabled={!state.client || computed.lineCount === 0}
            className="px-5 py-2.5 text-sm font-medium bg-surface border border-border text-text-primary rounded hover:bg-surface-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Preview
          </button>
          <button
            onClick={async () => {
              if (discountMissingReason) {
                toast('Enter a reason for the discount', 'error');
                return;
              }
              try {
                await generateQuotePdf({ state: pdfState, computed, activeTerms, settings });
                toast('Quote PDF downloaded');
              } catch (err) {
                console.error('PDF generation failed:', err);
                toast('Failed to generate PDF', 'error');
              }
            }}
            disabled={!state.client || computed.lineCount === 0}
            className="px-5 py-2.5 text-sm font-medium bg-surface border border-accent/40 text-accent rounded hover:bg-accent/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isMobile ? 'PDF' : 'Download PDF'}
          </button>
          <button
            onClick={async () => { await saveDraftAsync(); }}
            disabled={createQuote.isPending || updateQuoteMut.isPending || computed.lineCount === 0}
            className="px-4 py-2.5 text-sm font-medium bg-surface border border-border text-text-muted rounded hover:bg-surface-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {(createQuote.isPending || updateQuoteMut.isPending) ? 'Saving...' : (state.sourceQuoteId && state.sourceQuoteStatus === 'draft' ? 'Update Draft' : 'Save Draft')}
          </button>
          <FocusHide k="quote.saveAndSend">
          <button
            onClick={async () => {
              const saved = await saveDraftAsync();
              if (saved?.id) setSendQuoteId(saved.id);
            }}
            disabled={
              createQuote.isPending ||
              updateQuoteMut.isPending ||
              computed.lineCount === 0 ||
              !state.client ||
              !state.email
            }
            title={
              !state.email
                ? 'Add a client email to send the quote'
                : 'Save the current draft and open the Send Quote pre-flight modal'
            }
            className="px-4 py-2.5 text-sm font-medium bg-accent text-bg rounded hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ✉ Save & Send
          </button>
          </FocusHide>
          <button
            onClick={() => dispatch({ type: 'RESET' })}
            className="btn-ghost px-4 py-2.5 text-sm"
          >
            Clear
          </button>

          {/* Total display — hidden on mobile since bottom bar shows it */}
          <div className="ml-auto text-right hidden md:block">
            <div className="text-xs text-text-dim">Quote Total</div>
            <div className="text-xl font-bold text-accent">{fmtC(computed.total)}</div>
          </div>
        </div>
      </div>

      {/* ── Right: Preview Panel (desktop only) ── */}
      <div className="hidden md:block w-72 lg:w-80 flex-shrink-0 overflow-y-auto">
        <QuotePreview
          state={state}
          computed={computed}
          activeTerms={activeTerms}
        />
      </div>

      {/* ── Mobile: Bottom Summary Bar ── */}
      <div className="fixed left-0 right-0 md:hidden bg-surface border-t border-border px-4 py-3 flex items-center justify-between z-[1100] shadow-lg" style={{ bottom: 'calc(56px + env(safe-area-inset-bottom, 0px))' }}>
        <div className="text-sm text-text-primary">
          <span className="font-semibold">{computed.lineCount} item{computed.lineCount !== 1 ? 's' : ''}</span>
          <span className="mx-2">&middot;</span>
          <span className="text-accent font-bold">{fmtC(computed.total)}</span>
        </div>
        <button
          onClick={() => setPreviewOpen(true)}
          className="px-3 py-1.5 text-xs font-medium bg-accent text-white rounded hover:bg-accent-hover transition-colors"
        >
          Preview &#9650;
        </button>
      </div>

      {/* ── Mobile: Preview Bottom Sheet ── */}
      {previewOpen && (
        <div className="fixed inset-0 z-[1300] md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPreviewOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto bg-surface rounded-t-xl border-t border-border shadow-xl">
            <div className="sticky top-0 bg-surface px-4 py-3 border-b border-border flex items-center justify-between z-10">
              <span className="text-sm font-heading text-text-primary uppercase tracking-wide">Quote Preview</span>
              <button
                onClick={() => setPreviewOpen(false)}
                className="text-text-muted text-xs hover:text-text-primary transition-colors"
              >
                Close &#9660;
              </button>
            </div>
            <QuotePreview state={state} computed={computed} activeTerms={activeTerms} />
          </div>
        </div>
      )}

      {/* Confirmation dialog */}
      {showConfirm && (
        <CreateJobConfirmDialog
          state={pdfState}
          computed={computed}
          onConfirm={handleConfirmCreateJob}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      {/* Linked job belongs to a different client — offer a separate job, or a
          confirmed client change */}
      {identityBlock && (
        <JobIdentityBlockDialog
          block={identityBlock}
          saving={saving}
          onCreateSeparate={() => handleConfirmCreateJob({ forceNewJob: true })}
          onOverride={() => handleConfirmCreateJob({ allowIdentityChange: true })}
          onCancel={() => setIdentityBlock(null)}
        />
      )}

      {/* Add Client dialog */}
      {showAddClient && (
        <AddClientDialog
          onCreated={(newContact) => {
            setShowAddClient(false);
            handleClientSelect(newContact);
          }}
          onCancel={() => setShowAddClient(false)}
        />
      )}

      <PdfPreviewModal
        open={!!pdfPreview}
        blob={pdfPreview?.blob}
        filename={pdfPreview?.filename}
        onClose={() => setPdfPreview(null)}
      />
      {sendQuoteId && (
        <SendQuoteModal
          quote={allQuotes.find((q) => q.id === sendQuoteId)}
          job={(() => {
            const q = allQuotes.find((q) => q.id === sendQuoteId);
            return q?.jobId ? jobs.find((j) => j.id === q.jobId) : null;
          })()}
          onClose={() => setSendQuoteId(null)}
        />
      )}
    </div>
  );
}
